import { useCallback, useEffect, useState } from 'react';
import type { BackstageIdentityApi, IdentityApi, ProfileInfoApi, SessionApi } from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { Button, Typography, makeStyles } from '@material-ui/core';
import { manewcoTokens } from '@platform/plugin-platform-react';
import { LogoFull } from '../nav/LogoFull';

const useStyles = makeStyles(theme => {
  const MANEWCO = manewcoTokens(theme);
  return {
    page: {
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      padding: '24px 16px',
      backgroundColor: MANEWCO.background,
    },
    // Maquette « Clarté complète » (.login.card) : carte centrée de 500 px.
    card: {
      width: '100%',
      maxWidth: 500,
      padding: '40px 32px',
      textAlign: 'center',
      backgroundColor: MANEWCO.surface,
      border: `1px solid ${MANEWCO.line}`,
      borderRadius: MANEWCO.radiusCard,
      boxShadow: MANEWCO.shadow,
    },
    brand: { display: 'flex', justifyContent: 'center', marginBottom: 28 },
    title: { fontSize: 31, fontWeight: 700, letterSpacing: -1, lineHeight: 1.3, color: MANEWCO.text },
    text: { margin: '18px 0 28px', color: MANEWCO.textSecondary, lineHeight: 1.7 },
    button: { width: '100%', minHeight: 44 },
    error: { marginTop: 16, color: MANEWCO.tones.error.main, fontSize: 12 },
  };
});

type AuthApi = BackstageIdentityApi & ProfileInfoApi & SessionApi;

interface Props {
  authApi: AuthApi;
  onSignInSuccess(identityApi: IdentityApi): void;
}

/**
 * Page de connexion de la maquette : un bouton « Continuer avec Manewco » qui lance la connexion
 * OIDC (Keycloak, realm manewco). Une session existante reconnecte sans clic.
 */
export const ManewcoSignInPage = ({ authApi, onSignInSuccess }: Props) => {
  const classes = useStyles();
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const finish = useCallback(
    async (identity: Awaited<ReturnType<AuthApi['getBackstageIdentity']>>) => {
      if (!identity) throw new Error('aucune identité renvoyée');
      const profile = await authApi.getProfile();
      onSignInSuccess(UserIdentity.create({ identity: identity.identity, authApi, profile }));
    },
    [authApi, onSignInSuccess],
  );

  useEffect(() => {
    let cancelled = false;
    authApi
      .getBackstageIdentity({ optional: true })
      .then(identity => (identity && !cancelled ? finish(identity) : undefined))
      .catch(() => undefined)
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, [authApi, finish]);

  const signIn = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await finish(await authApi.getBackstageIdentity({ instantPopup: true }));
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setBusy(false);
    }
  };

  return (
    <main className={classes.page}>
      <section className={classes.card}>
        <div className={classes.brand}>
          <LogoFull />
        </div>
        <Typography component="h1" className={classes.title}>
          Votre plateforme, au même endroit.
        </Typography>
        <Typography className={classes.text}>
          Connectez-vous avec votre compte Manewco pour retrouver vos applications et votre espace de travail.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={signIn}
          disabled={checking || busy}
        >
          Continuer avec Manewco
        </Button>
        {error && (
          <Typography role="alert" className={classes.error}>
            {`Connexion impossible : ${error}`}
          </Typography>
        )}
      </section>
    </main>
  );
};
